// Section 13: crime rolls and their effects. Each grown-up (adults and elders, E1-04) not in
// custody or about to be exiled rolls their crimeRisk once per season in sorted id order
// (`rng.chance`); a success draws the crime type by weight (`rng.weighted`, core/queries
// crimeTypeWeights), the effect happens, and detection is rolled:
//   theft of food   2-6 food from stock (flow `theft`) to the thief's household, who eat it: their
//                   food satisfaction this season rises toward 1 (E1-11)
//   theft of tools  1-2 tools lost from stock (flow `theft`)
//   assault         a grown-up outside the offender's household is injured; their household -15
//   sabotage        a building loses 20 condition (the `damageBuilding` delta, cause sabotage)
//   killing         a grown-up outside the household dies (violence); happiness -8 and trust -6
//                   for everyone
// Undetected crimes still happen and are reported with no offender ("unexplained loss"); an
// undetected killing is remembered in `state.safety` (Sections 6 and 20). Detected ones open a
// justice case.
import { isChild, peopleSorted, crimeTypeWeights, detectionChance } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type {
  BuildingId,
  CaseId,
  CrimeKind,
  Person,
  PersonId,
  PhaseContext,
  ReportEntry,
} from '../../core/types';
import { killPerson } from '../shared/death';
import { applyDelta } from '../shared/deltas';
import { remember } from '../shared/memory';
import { addStock } from '../shared/stocks';
import { num } from '../shared/value';
import { judge, openCase } from './cases';
import { changeTrust, type CrimeRun } from './trust';

interface Effect {
  victimId?: PersonId;
  buildingId?: BuildingId;
  amount?: number;
}

export interface CrimeOutcome extends Effect {
  crime: CrimeKind;
  offenderId: PersonId;
  detected: boolean;
  caseId?: CaseId;
}

/**
 * May this person commit a crime this season? Only judgements change custody or exile in this
 * phase, so anyone not judged yet is read from the committed state (still skipping the dead).
 */
export function canOffend(ctx: PhaseContext, run: CrimeRun, id: PersonId): boolean {
  const people = ctx.draft.people;
  if (!(id in people)) return false;
  const p = run.judged.has(id) ? people[id] : ctx.state.people[id];
  return (
    p !== undefined &&
    !isChild(ctx.cfg, p) &&
    p.status.inCustody === null &&
    p.plannedDeparture?.reason !== 'exile'
  );
}

/** Grown-ups outside the offender's household (victims of violence), sorted by id. */
function victimsFor(ctx: PhaseContext, offender: Person): Person[] {
  return peopleSorted(ctx.draft).filter(
    (q) => q.id !== offender.id && q.householdId !== offender.householdId && !isChild(ctx.cfg, q),
  );
}

/** The thief's household eats the stolen food: raise their food satisfaction toward 1. */
function feedHousehold(ctx: PhaseContext, offender: Person, amount: number): void {
  const report = ctx.report.current;
  const demand = report.demand.food ?? {};
  const members = [...(ctx.draft.households[offender.householdId]?.memberIds ?? [offender.id])];
  let left = amount;
  for (const id of members.sort()) {
    const needs = report.needs[id];
    const need = demand[id] ?? 0;
    if (needs === undefined || need <= 0 || left <= 0) continue;
    const eat = Math.min(left, need * (1 - needs.food));
    if (eat <= 0) continue;
    needs.food = Math.min(1, needs.food + eat / need);
    left -= eat;
  }
}

function applyCrime(
  ctx: PhaseContext,
  run: CrimeRun,
  offender: Person,
  crime: CrimeKind,
): Effect | null {
  const c = ctx.cfg.crime;
  switch (crime) {
    case 'theft-food': {
      const want = ctx.rng.int(c.theftFood.min, c.theftFood.max);
      const taken = -addStock(ctx, 'food', -want, 'theft');
      if (taken <= 0) return null;
      feedHousehold(ctx, offender, taken);
      return { amount: taken };
    }
    case 'theft-tools': {
      const want = ctx.rng.int(c.theftTools.min, c.theftTools.max);
      const taken = -addStock(ctx, 'tools', -want, 'theft');
      return taken > 0 ? { amount: taken } : null;
    }
    case 'assault': {
      const pool = victimsFor(ctx, offender);
      if (pool.length === 0) return null;
      const victim = ctx.rng.pick(pool);
      applyDelta(ctx, { kind: 'injure', personId: victim.id, source: 'crime:assault' });
      const household = ctx.draft.households[victim.householdId]?.memberIds ?? [victim.id];
      for (const id of [...household].sort()) changeTrust(ctx, run, id, 'crime-victim');
      return { victimId: victim.id };
    }
    case 'sabotage': {
      const standing = sortedValues(ctx.draft.buildings).filter((b) => b.condition > 0);
      if (standing.length === 0) return null;
      const building = ctx.rng.pick(standing);
      const amount = -c.sabotage.condition;
      applyDelta(ctx, {
        kind: 'damageBuilding',
        buildingId: building.id,
        cause: 'sabotage',
        amount,
        source: 'crime:sabotage',
      });
      return { buildingId: building.id, amount };
    }
    case 'killing': {
      const pool = victimsFor(ctx, offender);
      if (pool.length === 0) return null;
      const victim = ctx.rng.pick(pool);
      killPerson(ctx, victim.id, 'violence');
      applyDelta(ctx, {
        kind: 'everyone',
        field: 'happiness',
        amount: c.killing.happiness,
        source: 'crime:killing',
      });
      for (const p of peopleSorted(ctx.draft)) changeTrust(ctx, run, p.id, 'killing');
      return { victimId: victim.id };
    }
  }
}

/**
 * Commit one crime and roll its detection (`detected` forces the outcome, for tests). Returns
 * null when there was nothing to take or no one to hurt.
 */
export function commitCrime(
  ctx: PhaseContext,
  run: CrimeRun,
  offenderId: PersonId,
  crime: CrimeKind,
  opts: { detected?: boolean } = {},
): CrimeOutcome | null {
  const offender = ctx.draft.people[offenderId];
  if (offender === undefined) return null;
  const effect = applyCrime(ctx, run, offender, crime);
  if (effect === null) return null;
  const detected =
    opts.detected ??
    ctx.rng.happened(ctx.rng.chance(detectionChance(ctx.state, ctx.cfg, crime).value));
  const outcome: CrimeOutcome = { crime, offenderId, detected, ...effect };
  const turn = ctx.season.turn;
  // A surviving victim remembers it, and who did it if that came out.
  const victim = effect.victimId === undefined ? undefined : ctx.draft.people[effect.victimId];
  if (victim !== undefined)
    remember(
      ctx,
      victim,
      detected ? { turn, kind: 'victim', otherId: offenderId } : { turn, kind: 'victim' },
    );
  if (detected) {
    outcome.caseId = openCase(
      ctx,
      crime,
      offenderId,
      effect.victimId === undefined ? [] : [effect.victimId],
    );
  } else if (crime === 'killing') {
    ctx.draft.safety.lastUndetectedKillingTurn = turn;
  }
  const entry: Extract<ReportEntry, { kind: 'crime' }> = {
    kind: 'crime',
    crime,
    offenderId: detected ? offenderId : null,
    detected,
  };
  if (effect.victimId !== undefined) entry.victimId = effect.victimId;
  if (effect.amount !== undefined) entry.amount = effect.amount;
  if (effect.buildingId !== undefined) entry.buildingId = effect.buildingId;
  if (outcome.caseId !== undefined) entry.caseId = outcome.caseId;
  ctx.report.add(entry);
  if (outcome.caseId !== undefined) judge(ctx, run, outcome.caseId);
  return outcome;
}

/** Everyone's crime roll for the season, in sorted id order. */
export function rollCrimes(ctx: PhaseContext, run: CrimeRun): void {
  for (const snap of peopleSorted(ctx.state)) {
    if (!canOffend(ctx, run, snap.id)) continue;
    const risk = num(ctx, { kind: 'crimeRisk', personId: snap.id });
    if (!ctx.rng.happened(ctx.rng.chance(risk))) continue;
    const weights = crimeTypeWeights(ctx.state, ctx.cfg, snap).map((w) => ({
      item: w.crime,
      weight: w.weight,
    }));
    if (weights.length === 0) continue;
    commitCrime(ctx, run, snap.id, ctx.rng.weighted(weights));
  }
}
